import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  useEffect,
  ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Sentry } from "@/lib/sentry";

// --- Type Definitions ---

export interface AgentProfile {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  vertical: "life_insurance" | "mortgage";
  licenseNumber: string;
  licensedStates: string[];
  tier: "Bronze" | "Silver" | "Gold" | "Elite";
  isDemo: boolean;
}

export interface LeadPreference {
  leadTypeId: string;
  leadTypeName: string;
  vertical: "life_insurance" | "mortgage";
  basePrice: number;
  /** Internal pacing basis value used to derive velocity tier. Not a price. */
  bidValue: number;
  dailyCap: number;
  monthlyCap: number;
  isActive: boolean;
}

export interface PerformanceState {
  performanceScore: number;
  avgResponseTime: number;
  contactRate: number;
  closeRate: number;
}

export interface DeliveryTracking {
  leadsReceivedToday: number;
  leadsReceivedThisMonth: number;
  lastDeliveryDate: string | null;
}

export interface SecuritySettings {
  mfaEnabled: boolean;
  emailNotifications: boolean;
  smsNotifications: boolean;
}

// --- Context State ---

interface AgentState {
  profile: AgentProfile;
  preferences: LeadPreference[];
  performance: PerformanceState;
  deliveryTracking: DeliveryTracking;
  security: SecuritySettings;
  loaded: boolean;
  /** When admin is viewing as a different client (Phase D) */
  viewAsAgentId: string | null;
}

// --- Context Actions ---

interface AgentActions {
  // Profile & Settings
  updateProfile: (data: Partial<AgentProfile>) => void;
  updateLicensedStates: (states: string[]) => void;
  updateSecuritySettings: (settings: Partial<SecuritySettings>) => void;

  // Preferences
  updateLeadPreference: (leadTypeId: string, updates: Partial<LeadPreference>) => void;
  setPreferences: (preferences: LeadPreference[]) => void;
  toggleLeadTypeActive: (leadTypeId: string) => void;

  // Performance
  incrementResponseTime: (seconds: number) => void;
  updatePerformanceScore: (score: number) => void;
  refreshPerformance: (data: {
    performanceScore: number;
    avgResponseTime: number;
    contactRate: number;
    closeRate: number;
    tier: AgentProfile["tier"];
  }) => void;

  // Delivery Tracking
  incrementDailyDelivery: () => void;
  incrementMonthlyDelivery: () => void;
  resetDailyCounter: () => void;
  resetMonthlyCounter: () => void;
}

// --- Safe Default Data ---
// These defaults keep the UI functional while real data loads from the database.
// All values are zeroed — real data is fetched from Supabase on auth.

const initialAgentState: AgentState = {
  profile: {
    id: "",
    name: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    vertical: "life_insurance",
    licenseNumber: "",
    licensedStates: [],
    tier: "Silver",
    isDemo: false,
  },
  preferences: [],
  performance: {
    performanceScore: 0,
    avgResponseTime: 0,
    contactRate: 0,
    closeRate: 0,
  },
  deliveryTracking: {
    leadsReceivedToday: 0,
    leadsReceivedThisMonth: 0,
    lastDeliveryDate: null,
  },
  security: {
    mfaEnabled: false,
    emailNotifications: true,
    smsNotifications: true,
  },
  loaded: false,
  viewAsAgentId: null,
};

// --- Context ---

const AgentContext = createContext<{
  state: AgentState;
  actions: AgentActions;
} | undefined>(undefined);

// --- Provider ---

export const AgentProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<AgentState>(initialAgentState);
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // Reactively read viewAs from URL so re-navigation triggers a refetch
  const viewAsId = searchParams.get("viewAs") || null;

  // Fetch real agent data from the database when authenticated
  useEffect(() => {
    if (!user) {
      setState(initialAgentState);
      return;
    }

    const fetchAgent = async () => {
      try {
        let agentRow: any = null;

        // Phase D: If viewAs is set, fetch the target agent directly.
        // Admin RLS allows reading any agent row via is_admin_user().
        if (viewAsId) {
          const { data: viewAsAgent } = await supabase
            .from("agents")
            .select("*")
            .eq("id", viewAsId)
            .maybeSingle();

          if (viewAsAgent) {
            agentRow = viewAsAgent;
          } else {
            console.warn("[AgentContext] viewAs agent not found:", viewAsId);
            // Fall through to normal flow
          }
        }

        if (!agentRow) {
          // Primary: query by auth UUID (works when agent.id matches auth.uid())
          const { data: byId } = await supabase
            .from("agents")
            .select("*")
            .eq("id", user.id)
            .maybeSingle();

          if (byId) {
            agentRow = byId;

            // Proactively reclaim orphaned leads from deleted+recreated accounts.
            // resolve_agent_identity now handles this too, but calling reclaim
            // explicitly ensures it runs even if resolve_agent_identity returns
            // early (agent found by ID, no email mismatch).
            try {
              const { data: reclaimed } = await (supabase.rpc as any)("reclaim_orphaned_leads");
              if (reclaimed?.leads_reclaimed > 0) {
                console.log(`[AgentContext] Reclaimed ${reclaimed.leads_reclaimed} orphaned leads`);
              }
            } catch {
              // RPC might not exist yet — graceful degradation
            }
          } else {
            // Fallback: use SECURITY DEFINER RPC to find agent by email and reconcile.
            // RLS blocks email-based SELECT for regular users (policy: id = auth.uid()),
            // so only a server-side function can resolve the mismatch.
            try {
              const { data: resolved } = await (supabase.rpc as any)("resolve_agent_identity");
              if (resolved && (resolved.resolved_by === 'id' || resolved.resolved_by === 'email_reconciled')) {
                // RPC found and/or reconciled the agent — retry the query
                const { data: retryData } = await supabase
                  .from("agents")
                  .select("*")
                  .eq("id", user.id)
                  .maybeSingle();
                agentRow = retryData;
              }
            } catch {
              // RPC might not exist yet — graceful degradation
            }
          }
        }

        if (agentRow) {
          const nameParts = (agentRow.name || "").split(" ");
          const firstName = nameParts[0] || user.email?.split("@")[0] || "Agent";
          const lastName = nameParts.slice(1).join(" ") || "";
          const prefs = Array.isArray(agentRow.lead_preferences) ? agentRow.lead_preferences : [];
          const perfMetrics = agentRow.performance_metrics || {};

          setState((prev) => ({
            ...prev,
            viewAsAgentId: viewAsId || null,
            profile: {
              id: viewAsId || user.id, // Use viewAs target ID or auth UUID
              name: agentRow.name || firstName,
              firstName,
              lastName,
              email: viewAsId ? (agentRow.email || "") : (user.email || agentRow.email || ""),
              phone: agentRow.phone || prev.profile.phone,
              company: agentRow.company || prev.profile.company,
              vertical: agentRow.vertical || prev.profile.vertical,
              licenseNumber: agentRow.license_number || prev.profile.licenseNumber,
              licensedStates: agentRow.licensed_states || prev.profile.licensedStates,
              tier: agentRow.tier || prev.profile.tier,
              isDemo: agentRow.is_demo ?? false,
            },
            // Only override preferences if DB has them, otherwise keep safe defaults
            preferences: prefs.length > 0 ? prefs : prev.preferences,
            performance: {
              performanceScore: agentRow.performance_score ?? prev.performance.performanceScore,
              avgResponseTime: perfMetrics.avgResponseTime ?? prev.performance.avgResponseTime,
              contactRate: perfMetrics.contactRate ?? prev.performance.contactRate,
              closeRate: perfMetrics.closeRate ?? prev.performance.closeRate,
            },
            deliveryTracking: {
              leadsReceivedToday: agentRow.leads_received_today ?? prev.deliveryTracking.leadsReceivedToday,
              leadsReceivedThisMonth: agentRow.leads_received_this_month ?? prev.deliveryTracking.leadsReceivedThisMonth,
              lastDeliveryDate: prev.deliveryTracking.lastDeliveryDate,
            },
            security: {
              ...prev.security,
              emailNotifications: agentRow.email_notifications_enabled ?? prev.security.emailNotifications,
              smsNotifications: agentRow.sms_notifications_enabled ?? prev.security.smsNotifications,
            },
            loaded: true,
          }));

          // Tag Sentry errors with agent context for filtering in dashboard
          Sentry.setTag("agent.vertical", agentRow.vertical || "unknown");
          Sentry.setTag("agent.tier", agentRow.tier || "Bronze");
          Sentry.setTag("agent.is_demo", String(agentRow.is_demo ?? false));
        } else {
          // No agent record yet — update profile with auth info, keep safe defaults
          setState((prev) => ({
            ...prev,
            profile: {
              ...prev.profile,
              id: user.id,
              email: user.email || prev.profile.email,
              name: user.email?.split("@")[0] || prev.profile.name,
              firstName: user.email?.split("@")[0] || prev.profile.firstName,
            },
            loaded: true,
          }));
        }
      } catch (err) {
        console.error("[AgentContext] Failed to fetch agent:", err);
        Sentry.captureException(err, { tags: { component: "AgentContext" } });
        // On error, keep safe defaults and mark as loaded
        setState((prev) => ({ ...prev, loaded: true }));
      }
    };

    fetchAgent();
  }, [user, viewAsId]);

  // Profile & Settings Actions
  const updateProfile = useCallback((data: Partial<AgentProfile>) => {
    setState((prev) => ({
      ...prev,
      profile: { ...prev.profile, ...data },
    }));
  }, []);

  const updateLicensedStates = useCallback((states: string[]) => {
    setState((prev) => ({
      ...prev,
      profile: { ...prev.profile, licensedStates: states },
    }));
  }, []);

  const updateSecuritySettings = useCallback((settings: Partial<SecuritySettings>) => {
    setState((prev) => ({
      ...prev,
      security: { ...prev.security, ...settings },
    }));
  }, []);

  // Preferences Actions
  const updateLeadPreference = useCallback(
    (leadTypeId: string, updates: Partial<LeadPreference>) => {
      setState((prev) => ({
        ...prev,
        preferences: prev.preferences.map((pref) =>
          pref.leadTypeId === leadTypeId ? { ...pref, ...updates } : pref
        ),
      }));
    },
    []
  );

  const toggleLeadTypeActive = useCallback((leadTypeId: string) => {
    setState((prev) => ({
      ...prev,
      preferences: prev.preferences.map((pref) =>
        pref.leadTypeId === leadTypeId ? { ...pref, isActive: !pref.isActive } : pref
      ),
    }));
  }, []);

  // Bulk update all preferences
  const setPreferences = useCallback((preferences: LeadPreference[]) => {
    console.log("[AgentContext] Agent preferences saved to context");
    setState((prev) => ({
      ...prev,
      preferences,
    }));
  }, []);


  // Performance Actions
  const incrementResponseTime = useCallback((seconds: number) => {
    setState((prev) => {
      const totalLeads = prev.deliveryTracking.leadsReceivedToday;
      const newAvg = totalLeads > 0
        ? (prev.performance.avgResponseTime * totalLeads + seconds) / (totalLeads + 1)
        : seconds;
      return {
        ...prev,
        performance: { ...prev.performance, avgResponseTime: newAvg },
      };
    });
  }, []);

  const updatePerformanceScore = useCallback((score: number) => {
    setState((prev) => ({
      ...prev,
      performance: { ...prev.performance, performanceScore: Math.min(100, Math.max(0, score)) },
    }));
  }, []);

  const refreshPerformance = useCallback(
    (data: { performanceScore: number; avgResponseTime: number; contactRate: number; closeRate: number; tier: AgentProfile["tier"] }) => {
      setState((prev) => ({
        ...prev,
        profile: { ...prev.profile, tier: data.tier },
        performance: {
          performanceScore: Math.min(100, Math.max(0, data.performanceScore)),
          avgResponseTime: data.avgResponseTime,
          contactRate: data.contactRate,
          closeRate: data.closeRate,
        },
      }));
    },
    []
  );

  // Delivery Tracking Actions
  const incrementDailyDelivery = useCallback(() => {
    setState((prev) => ({
      ...prev,
      deliveryTracking: {
        ...prev.deliveryTracking,
        leadsReceivedToday: prev.deliveryTracking.leadsReceivedToday + 1,
        lastDeliveryDate: new Date().toISOString().split("T")[0],
      },
    }));
  }, []);

  const incrementMonthlyDelivery = useCallback(() => {
    setState((prev) => ({
      ...prev,
      deliveryTracking: {
        ...prev.deliveryTracking,
        leadsReceivedThisMonth: prev.deliveryTracking.leadsReceivedThisMonth + 1,
      },
    }));
  }, []);

  const resetDailyCounter = useCallback(() => {
    setState((prev) => ({
      ...prev,
      deliveryTracking: { ...prev.deliveryTracking, leadsReceivedToday: 0 },
    }));
  }, []);

  const resetMonthlyCounter = useCallback(() => {
    setState((prev) => ({
      ...prev,
      deliveryTracking: { ...prev.deliveryTracking, leadsReceivedThisMonth: 0 },
    }));
  }, []);

  const actions: AgentActions = {
    updateProfile,
    updateLicensedStates,
    updateSecuritySettings,
    updateLeadPreference,
    setPreferences,
    toggleLeadTypeActive,
    incrementResponseTime,
    updatePerformanceScore,
    refreshPerformance,
    incrementDailyDelivery,
    incrementMonthlyDelivery,
    resetDailyCounter,
    resetMonthlyCounter,
  };

  return (
    <AgentContext.Provider value={{ state, actions }}>
      {children}
    </AgentContext.Provider>
  );
};

// --- Hook ---

export const useAgent = () => {
  const context = useContext(AgentContext);
  if (context === undefined) {
    throw new Error("useAgent must be used within an AgentProvider");
  }
  return context;
};

export default AgentContext;
